import React, { useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import CustomTextInput from "../../../components/CustomTextInput";
import { useMutateCustomFieldsHandler } from "../dataHandlers/mutateCustomFieldsHandler";

export const CUSTOM_FIELDS_FIELD_NAME_REGEX = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export default function AddCustomFieldsModal({
  open,
  onClose,
  existingFieldNames = [],
}) {
  const schema = Yup.object().shape({
    field_name: Yup.string()
      .trim()
      .required("This field is required")
      .matches(
        CUSTOM_FIELDS_FIELD_NAME_REGEX,
        "Use strict snake_case: lowercase letters, numbers and single underscores (e.g. cost_center).",
      )
      .test(
        "unique",
        "A field with this name already exists.",
        (value) => !existingFieldNames.includes(value?.trim()),
      ),
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { field_name: "" },
  });

  const handleClose = () => {
    clearApiError();
    reset({ field_name: "" });
    onClose();
  };

  const {
    onSubmit: submitCustomFields,
    isPending,
    apiError,
    clearApiError,
  } = useMutateCustomFieldsHandler({
    onSuccess: handleClose,
  });

  useEffect(() => {
    if (apiError) {
      setError("field_name", { type: "server", message: apiError });
    }
  }, [apiError, setError]);

  const onSubmit = async ({ field_name }) => {
    await submitCustomFields(field_name);
  };

  const fieldName = watch("field_name");

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "32rem" },
          maxWidth: "600px",
          p: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Add Custom Field</Typography>
          <IconButton
            onClick={handleClose}
            disabled={isPending}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </Stack>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <CustomTextInput
            control={control}
            name="field_name"
            label="field_name"
            errors={errors}
            helperText="Acts as a unique key. Cannot be edited after saving."
            disabled={isPending}
          />

          <Stack
            direction="row"
            sx={{ justifyContent: "flex-end", gap: 1, mt: 3 }}
          >
            <Button onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending || !fieldName?.trim()}
            >
              {isPending ? "Processing..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Modal>
  );
}
